import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ReviewCard } from '../../components/reviews';
import ReasonModal from '../../components/reviews/ReasonModal';
import reviewService from '../../services/reviewService';
import './ReviewListPage.css';

function ReviewListPage() {
  // Review data state
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    flagged: 0,
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state (0-indexed for backend)
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('createdAt,desc');

  // Modal state
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Fetch reviews from API
  const fetchReviews = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await reviewService.getReviews({
        page: pagination.page,
        size: pagination.size,
        sort: sortOrder,
      });

      // Handle ApiResponse wrapper
      if (response.data.success && response.data.data) {
        const data = response.data.data;

        // Apply client-side filtering (backend doesn't support these filters)
        let filteredReviews = data.content;

        if (statusFilter) {
          filteredReviews = filteredReviews.filter(r => r.status === statusFilter);
        }
        if (ratingFilter) {
          filteredReviews = filteredReviews.filter(r => r.rating === parseInt(ratingFilter));
        }
        if (search) {
          const searchLower = search.toLowerCase();
          filteredReviews = filteredReviews.filter(r =>
            r.productName?.toLowerCase().includes(searchLower) ||
            r.userName?.toLowerCase().includes(searchLower) ||
            r.title?.toLowerCase().includes(searchLower) ||
            r.comment?.toLowerCase().includes(searchLower)
          );
        }

        setReviews(filteredReviews);
        setPagination(prev => ({
          ...prev,
          totalElements: data.totalElements,
          totalPages: data.totalPages,
        }));
      } else {
        throw new Error(response.data.message || 'Failed to load reviews');
      }
    } catch (err) {
      setError('Failed to load reviews. Please try again.');
      console.error('Error fetching reviews:', err);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.size, sortOrder, statusFilter, ratingFilter, search]);

  // Fetch stats from API
  const fetchStats = useCallback(async () => {
    try {
      const response = await reviewService.getReviewStats();
      if (response.data.success && response.data.data) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      // Don't show error for stats - non-critical
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [fetchReviews, fetchStats]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // === Action Handlers ===

  const handleApprove = async (reviewId) => {
    const confirmed = window.confirm(
      'Are you sure you want to approve this review? It will become visible on the ecommerce site.'
    );
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      const response = await reviewService.approveReview(reviewId);
      if (response.data.success) {
        toast.success('Review approved successfully');
        fetchReviews();
        fetchStats();
      } else {
        toast.error(response.data.message || 'Failed to approve review');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve review');
      console.error('Error approving review:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReject = (reviewId) => {
    setSelectedReviewId(reviewId);
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async (reason) => {
    setModalLoading(true);
    try {
      const response = await reviewService.rejectReview(selectedReviewId, reason);
      if (response.data.success) {
        toast.success('Review rejected successfully');
        setRejectModalOpen(false);
        setSelectedReviewId(null);
        fetchReviews();
        fetchStats();
      } else {
        toast.error(response.data.message || 'Failed to reject review');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject review');
      console.error('Error rejecting review:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleFlag = (reviewId) => {
    setSelectedReviewId(reviewId);
    setFlagModalOpen(true);
  };

  const handleFlagConfirm = async (reason) => {
    setModalLoading(true);
    try {
      const response = await reviewService.flagReview(selectedReviewId, reason);
      if (response.data.success) {
        toast.success('Review flagged for escalation');
        setFlagModalOpen(false);
        setSelectedReviewId(null);
        fetchReviews();
        fetchStats();
      } else {
        toast.error(response.data.message || 'Failed to flag review');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to flag review');
      console.error('Error flagging review:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleUnpublish = (reviewId) => {
    setSelectedReviewId(reviewId);
    setUnpublishModalOpen(true);
  };

  const handleUnpublishConfirm = async (reason) => {
    setModalLoading(true);
    try {
      const response = await reviewService.unpublishReview(selectedReviewId, reason);
      if (response.data.success) {
        toast.success('Review unpublished successfully');
        setUnpublishModalOpen(false);
        setSelectedReviewId(null);
        fetchReviews();
        fetchStats();
      } else {
        toast.error(response.data.message || 'Failed to unpublish review');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to unpublish review');
      console.error('Error unpublishing review:', err);
    } finally {
      setModalLoading(false);
    }
  };

  // === Filter & Pagination Handlers ===

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleRatingFilterChange = (value) => {
    setRatingFilter(value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setRatingFilter('');
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const hasActiveFilters = search || statusFilter || ratingFilter;

  // === Render ===

  return (
    <div className="review-list-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Reviews</h1>
          <p>Moderate customer reviews and manage feedback.</p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => { fetchReviews(); fetchStats(); }}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className="stat-card">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total Reviews</span>
        </div>
        <div className="stat-card stat-pending">
          <span className="stat-value">{stats.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card stat-approved">
          <span className="stat-value">{stats.approved}</span>
          <span className="stat-label">Approved</span>
        </div>
        <div className="stat-card stat-rejected">
          <span className="stat-value">{stats.rejected}</span>
          <span className="stat-label">Rejected</span>
        </div>
        <div className="stat-card stat-flagged">
          <span className="stat-value">{stats.flagged}</span>
          <span className="stat-label">Flagged</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search reviews..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="FLAGGED">Flagged</option>
          </select>

          <select
            value={ratingFilter}
            onChange={(e) => handleRatingFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>

          <select
            value={sortOrder}
            onChange={(e) => handleSortChange(e.target.value)}
            className="filter-select"
          >
            <option value="createdAt,desc">Newest First</option>
            <option value="createdAt,asc">Oldest First</option>
            <option value="rating,desc">Highest Rating</option>
            <option value="rating,asc">Lowest Rating</option>
          </select>

          {hasActiveFilters && (
            <button className="btn btn-text" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Pending Alert */}
      {stats.pending > 0 && !statusFilter && (
        <div className="pending-alert">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span>You have {stats.pending} review{stats.pending !== 1 ? 's' : ''} awaiting moderation.</span>
          <button className="btn btn-warning-sm" onClick={() => handleStatusFilterChange('PENDING')}>
            View Pending
          </button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={fetchReviews}>Try again</button>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading reviews...</span>
        </div>
      ) : reviews.length === 0 ? (
        <div className="empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
          </svg>
          <h3>No reviews found</h3>
          <p>Try adjusting your search or filter criteria.</p>
        </div>
      ) : (
        <>
          <div className="reviews-list">
            {reviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                onApprove={handleApprove}
                onReject={handleReject}
                onFlag={handleFlag}
                onUnpublish={handleUnpublish}
                isUpdating={isUpdating}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {pagination.page * pagination.size + 1} to{' '}
                {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of{' '}
                {pagination.totalElements} reviews
              </div>
              <div className="pagination-controls">
                <button
                  className="btn-page"
                  disabled={pagination.page === 0}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                  Previous
                </button>
                <div className="page-numbers">
                  {Array.from({ length: pagination.totalPages }, (_, i) => i).map((page) => (
                    <button
                      key={page}
                      className={`btn-page-number ${pagination.page === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page + 1}
                    </button>
                  ))}
                </div>
                <button
                  className="btn-page"
                  disabled={pagination.page === pagination.totalPages - 1}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <ReasonModal
        isOpen={rejectModalOpen}
        actionType="reject"
        title="Reject Review"
        onConfirm={handleRejectConfirm}
        onCancel={() => { setRejectModalOpen(false); setSelectedReviewId(null); }}
        isLoading={modalLoading}
      />

      <ReasonModal
        isOpen={flagModalOpen}
        actionType="flag"
        title="Flag Review"
        onConfirm={handleFlagConfirm}
        onCancel={() => { setFlagModalOpen(false); setSelectedReviewId(null); }}
        isLoading={modalLoading}
      />

      <ReasonModal
        isOpen={unpublishModalOpen}
        actionType="unpublish"
        title="Unpublish Review"
        onConfirm={handleUnpublishConfirm}
        onCancel={() => { setUnpublishModalOpen(false); setSelectedReviewId(null); }}
        isLoading={modalLoading}
      />
    </div>
  );
}

export default ReviewListPage;
